/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled, { css } from "styled-components";
import { SVGIcon, theme } from "@nordcloud/gnui";
import { show } from "~/components/Styled/animation";

type BadgeProps = {
  color?: string;
  onClick?: () => void;
  text: string;
  onRemove?: (values: string) => void;
};

const CloseWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  height: 1rem;
  padding-right: 0.5rem;
  cursor: pointer;
  > svg {
    fill: ${theme.color.text.text04};
  }
`;

const StyledBadge = styled.div<BadgeProps>`
  display: flex;
  float: left;
  align-items: center;
  padding: 0;
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5rem;
  background: ${theme.color.interactive.secondary};
  border-radius: ${theme.radius.xxl};
  margin: 4px 0;
  opacity: 0;
  transition: ease;
  animation: ${show} 0.2s forwards;
  span {
    color: ${theme.color.text.text04};
    margin: 0 ${theme.spacing.spacing03};
  }
  ${({ onClick }) =>
    onClick &&
    css`
      &:hover {
        opacity: 0.3;
        cursor: pointer;
      }
      &:active {
        background: ${theme.color.interactive.primaryActive};
        cursor: pointer;
      }
    `}
  ${({ color }) =>
    color &&
    css`
      background: ${color};
      &:hover {
        opacity: 0.8;
      }
    `}
`;

export function Badge({
  text,
  onClick,
  color,
  onRemove,
  ...props
}: BadgeProps) {
  return (
    <StyledBadge
      text={text}
      onClick={onRemove ? () => onRemove(text) : onClick}
      color={color}
      {...props}
    >
      <span>{text}</span>
      {onRemove && (
        <>
          <input type="hidden" defaultValue={text} />
          <CloseWrap>
            <SVGIcon name="close" size="sm" />
          </CloseWrap>
        </>
      )}
    </StyledBadge>
  );
}
